// Colocated Driver.js tour for the Manage Partners page, targeted at exec
// admins on their first visit. Walks the onboarding story end to end:
// register a partner organization, look it over on the list, invite the
// super admin who will run it, and off-board later with Deactivate. Ends
// with the shared outro variant for wallet-less roles so the security
// narrative names sign-in activity, permissions, and logout without
// mentioning a transaction PIN the exec admin does not have.

import { buildFaqOutroWithoutPinSteps } from '../../hooks/faqOutroSteps';

export const MANAGE_PARTNERS_OVERVIEW_TOUR_ID = 'manage-partners-overview-v1';

// Each CTA button and row action is rendered in more than one place across
// responsive breakpoints (desktop table, tablet table, mobile list), so the
// same data-tour attribute matches several nodes at once and only one is
// visible per viewport. Returning the visible one keeps the spotlight tight
// on the actual clickable element.
const visibleElementFor = (selector) => () => {
    const candidates = document.querySelectorAll(selector);
    for (const candidate of candidates) {
        if (candidate.offsetParent !== null) return candidate;
    }
    return null;
};

export const buildManagePartnersOverviewTourSteps = ({ completedTourIds = [] } = {}) => [
    {
        title: 'Welcome, exec admin',
        description: 'This is where you onboard partner organizations and hand each one to the super admin who will run it day to day. We will walk the page in a few short steps.',
    },
    {
        element: visibleElementFor('[data-tour="manage-partners-add-button"]'),
        title: 'Step 1: Register a partner',
        description: 'Add Partner opens a form for the organization name and details. Once you save, the partner appears on the list as pending until you invite their super admin.',
        side: 'bottom',
        align: 'end',
    },
    {
        element: '[data-tour="manage-partners-list"]',
        title: 'The partners you manage',
        description: 'Every partner on your account shows up here with its status: pending while it is being set up, active once fully onboarded, and archived after deactivation. Use the arrows at the bottom of the card if the list grows past one page.',
        side: 'top',
        align: 'center',
    },
    {
        element: visibleElementFor('[data-tour="manage-partners-super-admin-button"]'),
        title: 'Step 2: Invite the super admin',
        description: 'Open Super Admin on a partner to see who currently runs it or invite a new one. The person you invite receives a verification email and completes setup themselves. While their status still reads Pending you can resend that email or fix a typo in the address, both from inside this panel. From there they take over: they invite their own team, give each user a wallet, and set spending limits. Your part of the onboarding ends once the super admin is in place.',
        side: 'top',
        align: 'end',
        // Skipped when the list has no partner rows yet (first visit), so
        // the tour still fires end to end on an empty account.
        skipMissingElement: true,
    },
    {
        element: visibleElementFor('[data-tour="manage-partners-deactivate-button"]'),
        title: 'When a partner leaves',
        description: 'Deactivate freezes a partner so no one on their team can sign in. Nothing is deleted: their history, ledgers, and users all stay. Only exec admins can deactivate, and only while the partner is active.',
        side: 'top',
        align: 'end',
        // Only rendered on active partners, so skipped when the list has no
        // active partner row yet. The step reappears once one is active.
        skipMissingElement: true,
    },
    ...buildFaqOutroWithoutPinSteps({ completedTourIds }),
];
